/**
 * Archivos de Carga Automática
 *
 * Muestra estudios agrupados detectados en una carpeta
 * Vista simplificada: solo grupos de estudio (no archivos individuales)
 * Permite edición de datos del paciente a nivel de grupo antes de procesar
 */

import React, { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Files,
  FileCheck,
  Clock,
  AlertCircle,
  Loader2,
  Check,
  Play,
  SkipForward,
  Edit,
  RefreshCw,
  User,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  Calendar,
  FileImage,
  Building2,
} from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/presentation/components/ui/Dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/presentation/components/ui/Tabs'
import {
  useHotFolder,
  useHotFolderFiles,
  useHotFolderStats,
  useScanHotFolder,
  useResetHotFolder,
  useSkipFile,
  useUpdateOverride,
  formatDicomPatientName,
  formatDicomDate,
} from '@/application/hot-folders'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import { APP_ROUTES, DOCUMENT_TYPE_LABELS, getModalityDisplayName } from '@/config/constants'
import type {
  HotFolderFile,
  UpdateOverrideData,
} from '@/domain/hot-folder/types'
import { GroupProcessPreviewModal } from '@/presentation/components/hot-folder/GroupProcessPreviewModal'
import type { DocumentType } from '@/config/constants'

// =============================================================================
// Constants
// =============================================================================

const DOCUMENT_TYPE_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  ...Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
]

const SEX_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Femenino' },
  { value: 'O', label: 'Otro' },
]

// =============================================================================
// Types
// =============================================================================

interface StudyGroup {
  studyUid: string
  files: HotFolderFile[]
  patientName: string
  documentType: string
  documentNumber: string
  birthDate: string
  sex: string
  modality: string
  studyDate: string
  studyDescription: string
  institutionName: string
  imageCount: number
  hasOverrides: boolean
}

// =============================================================================
// Group Edit Modal - Edita paciente para todos los archivos del grupo
// =============================================================================

interface GroupEditModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  group: StudyGroup | null
  onSuccess?: () => void
}

function GroupEditModal({
  open,
  onOpenChange,
  group,
  onSuccess,
}: GroupEditModalProps) {
  const [documentType, setDocumentType] = useState<DocumentType | ''>('')
  const [documentNumber, setDocumentNumber] = useState('')
  const [firstName, setFirstName] = useState('')
  const [middleName, setMiddleName] = useState('')
  const [lastName, setLastName] = useState('')
  const [secondLastName, setSecondLastName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [sex, setSex] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const updateOverrideMutation = useUpdateOverride()

  // Initialize form with group data
  React.useEffect(() => {
    if (open && group) {
      const firstFile = group.files[0]

      // Get existing overrides or parse from DICOM
      setDocumentType((firstFile?.overrideDocumentType as DocumentType) || '')
      setDocumentNumber(firstFile?.overrideDocumentNumber || firstFile?.dicomPatientId || '')

      // Parse DICOM patient name (format: "LASTNAME^FIRSTNAME^MIDDLENAME" or "LASTNAME FIRSTNAME")
      let dicomFirstName = ''
      let dicomMiddleName = ''
      let dicomLastName = ''
      let dicomSecondLastName = ''

      if (firstFile?.dicomPatientName) {
        const nameParts = firstFile.dicomPatientName.includes('^')
          ? firstFile.dicomPatientName.split('^')
          : firstFile.dicomPatientName.split(' ')

        if (nameParts.length >= 1) dicomLastName = nameParts[0]?.trim() || ''
        if (nameParts.length >= 2) dicomFirstName = nameParts[1]?.trim() || ''
        if (nameParts.length >= 3) dicomMiddleName = nameParts[2]?.trim() || ''
        if (nameParts.length >= 4) dicomSecondLastName = nameParts[3]?.trim() || ''
      }

      setFirstName(firstFile?.overridePatientFirstName || dicomFirstName)
      setMiddleName(firstFile?.overridePatientMiddleName || dicomMiddleName)
      setLastName(firstFile?.overridePatientLastName || dicomLastName)
      setSecondLastName(firstFile?.overridePatientSecondLastName || dicomSecondLastName)

      // Parse DICOM birth date (format: "YYYYMMDD" -> "YYYY-MM-DD")
      let dicomBirthDate = ''
      if (firstFile?.dicomPatientBirthDate && firstFile.dicomPatientBirthDate.length === 8) {
        const year = firstFile.dicomPatientBirthDate.substring(0, 4)
        const month = firstFile.dicomPatientBirthDate.substring(4, 6)
        const day = firstFile.dicomPatientBirthDate.substring(6, 8)
        dicomBirthDate = `${year}-${month}-${day}`
      }

      setBirthDate(firstFile?.overridePatientBirthDate || dicomBirthDate)
      setSex(firstFile?.overridePatientSex || firstFile?.dicomPatientSex || '')
    }
    setError(null)
  }, [open, group])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!group || group.files.length === 0) return

    setError(null)
    setIsSubmitting(true)

    const data: UpdateOverrideData = {}
    if (documentType) data.overrideDocumentType = documentType as DocumentType
    if (documentNumber) data.overrideDocumentNumber = documentNumber.trim()
    if (firstName) data.overridePatientFirstName = firstName.trim()
    if (middleName) data.overridePatientMiddleName = middleName.trim()
    if (lastName) data.overridePatientLastName = lastName.trim()
    if (secondLastName) data.overridePatientSecondLastName = secondLastName.trim()
    if (birthDate) data.overridePatientBirthDate = birthDate
    if (sex) data.overridePatientSex = sex

    try {
      // Update ALL files in the group with the same patient data
      await Promise.all(
        group.files.map(file =>
          updateOverrideMutation.mutateAsync({ fileId: file.id, data })
        )
      )

      toast.success(`Datos del paciente actualizados para ${group.files.length} archivo(s)`)
      onOpenChange(false)
      onSuccess?.()
    } catch (err) {
      const message = getErrorMessage(err, 'Error al guardar los datos')
      setError(message)
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const firstFile = group?.files[0]
  const dicomPatientName = firstFile?.dicomPatientName
    ? formatDicomPatientName(firstFile.dicomPatientName)
    : 'Sin nombre'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>Editar Datos del Paciente</DialogTitle>
          <DialogDescription>
            Los cambios se aplicarán a los {group?.files.length || 0} archivo(s) de este estudio.
          </DialogDescription>
        </DialogHeader>

        {/* DICOM Data Summary */}
        <div className="bg-[var(--background-secondary)] rounded-md p-3 text-sm space-y-1">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <p><strong>Nombre DICOM:</strong> {dicomPatientName}</p>
            <p><strong>ID DICOM:</strong> {firstFile?.dicomPatientId || 'N/A'}</p>
            <p><strong>Fecha Nac. DICOM:</strong> {firstFile?.dicomPatientBirthDate ? formatDicomDate(firstFile.dicomPatientBirthDate) : 'N/A'}</p>
            <p><strong>Sexo DICOM:</strong> {firstFile?.dicomPatientSex || 'N/A'}</p>
            <p><strong>Modalidad:</strong> {getModalityDisplayName(firstFile?.dicomModality)}</p>
            <p><strong>Archivos:</strong> {group?.files.length || 0}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Tipo de Documento
              </label>
              <Select
                options={DOCUMENT_TYPE_OPTIONS}
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value as DocumentType | '')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Número de Documento
              </label>
              <Input
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                placeholder="123456789"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Primer Nombre
              </label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Juan"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Segundo Nombre
              </label>
              <Input
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
                placeholder="Carlos"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Primer Apellido
              </label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Pérez"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Segundo Apellido
              </label>
              <Input
                value={secondLastName}
                onChange={(e) => setSecondLastName(e.target.value)}
                placeholder="García"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Fecha de Nacimiento
              </label>
              <Input
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Sexo
              </label>
              <Select
                options={SEX_OPTIONS}
                value={sex}
                onChange={(e) => setSex(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Guardar
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Study Group Card - Tarjeta de estudio agrupado
// =============================================================================

interface StudyGroupCardProps {
  group: StudyGroup
  onEdit: (group: StudyGroup) => void
  onProcess: (group: StudyGroup) => void
  onSkip: (group: StudyGroup) => void
  isSkipping: boolean
}

function StudyGroupCard({
  group,
  onEdit,
  onProcess,
  onSkip,
  isSkipping,
}: StudyGroupCardProps) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left: Patient & Study Info */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* Patient Name & Edit */}
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-lg truncate">
                    {group.patientName}
                  </h3>
                  {group.hasOverrides && (
                    <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300 border-green-300">
                      Editado
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-[var(--foreground-muted)]">
                  {group.documentType ? `${DOCUMENT_TYPE_LABELS[group.documentType as DocumentType] || group.documentType}: ` : ''}
                  {group.documentNumber || 'Sin documento'}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(group)}
                className="shrink-0"
              >
                <Edit className="h-4 w-4 mr-1" />
                Editar
              </Button>
            </div>

            {/* Study Details Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Badge variant="default" className="font-mono">
                  {getModalityDisplayName(group.modality)}
                </Badge>
              </div>

              {group.studyDate && (
                <div className="flex items-center gap-2 text-[var(--foreground-muted)]">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDicomDate(group.studyDate)}</span>
                </div>
              )}

              <div className="flex items-center gap-2 text-[var(--foreground-muted)]">
                <FileImage className="h-4 w-4" />
                <span>{group.imageCount} {group.imageCount === 1 ? 'imagen' : 'imágenes'}</span>
              </div>

              {group.institutionName && (
                <div className="flex items-center gap-2 text-[var(--foreground-muted)]">
                  <Building2 className="h-4 w-4" />
                  <span className="truncate">{group.institutionName}</span>
                </div>
              )}
            </div>

            {/* Study Description */}
            {group.studyDescription && (
              <p className="text-sm text-[var(--foreground-muted)] truncate">
                {group.studyDescription}
              </p>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex flex-col gap-2 shrink-0">
            <Button
              onClick={() => onProcess(group)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Play className="h-4 w-4 mr-2" />
              Cargar Estudio
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSkip(group)}
              disabled={isSkipping}
              className="text-[var(--foreground-muted)]"
            >
              {isSkipping ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <SkipForward className="h-4 w-4 mr-1" />
              )}
              Omitir
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function HotFolderFilesPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // State
  const [activeTab, setActiveTab] = useState<'pending' | 'processed'>('pending')
  const [editingGroup, setEditingGroup] = useState<StudyGroup | null>(null)
  const [skippingGroupId, setSkippingGroupId] = useState<string | null>(null)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [groupToProcess, setGroupToProcess] = useState<HotFolderFile[] | null>(null)

  // Queries
  const { data: hotFolder } = useHotFolder(id)
  const { data: stats, refetch: refetchStats } = useHotFolderStats(id)

  // Get pending files
  const { data: pendingFiles, refetch: refetchPending } = useHotFolderFiles(id, {
    status: 'PENDING_REVIEW',
  })

  // Get detected files (new, not yet reviewed)
  const { data: detectedFiles, refetch: refetchDetected } = useHotFolderFiles(id, {
    status: 'DETECTED',
  })

  // Get processing files (in progress)
  const { data: processingFiles, refetch: refetchProcessing } = useHotFolderFiles(id, {
    status: 'PROCESSING',
  })

  // Get processed files
  const { data: processedFiles, refetch: refetchProcessed } = useHotFolderFiles(id, {
    status: 'COMPLETED',
  })

  // Combine pending, detected, and processing for "pending" tab
  const allPendingFiles = useMemo(() => {
    const pending = Array.isArray(pendingFiles) ? pendingFiles : []
    const detected = Array.isArray(detectedFiles) ? detectedFiles : []
    const processing = Array.isArray(processingFiles) ? processingFiles : []
    return [...detected, ...pending, ...processing]
  }, [pendingFiles, detectedFiles, processingFiles])

  // Group pending files by Study Instance UID and create StudyGroup objects
  const studyGroups = useMemo((): StudyGroup[] => {
    const groups: Record<string, HotFolderFile[]> = {}

    allPendingFiles.forEach((file) => {
      // Use study UID if available, otherwise create unique key per file
      const key = file.dicomStudyInstanceUid || `no-study-${file.id}`
      if (!groups[key]) groups[key] = []
      groups[key].push(file)
    })

    // Convert to StudyGroup objects
    return Object.entries(groups)
      .map(([studyUid, files]): StudyGroup => {
        const firstFile = files[0]

        // Get patient name from overrides or DICOM
        let patientName = 'Sin nombre'
        if (firstFile.overridePatientFirstName || firstFile.overridePatientLastName) {
          const parts = [
            firstFile.overridePatientFirstName,
            firstFile.overridePatientMiddleName,
            firstFile.overridePatientLastName,
            firstFile.overridePatientSecondLastName,
          ].filter(Boolean)
          patientName = parts.join(' ')
        } else if (firstFile.dicomPatientName) {
          patientName = formatDicomPatientName(firstFile.dicomPatientName)
        }

        // Check if any overrides exist
        const hasOverrides = !!(
          firstFile.overrideDocumentType ||
          firstFile.overrideDocumentNumber ||
          firstFile.overridePatientFirstName ||
          firstFile.overridePatientLastName
        )

        return {
          studyUid,
          files,
          patientName,
          documentType: firstFile.overrideDocumentType || '',
          documentNumber: firstFile.overrideDocumentNumber || firstFile.dicomPatientId || '',
          birthDate: firstFile.overridePatientBirthDate || firstFile.dicomPatientBirthDate || '',
          sex: firstFile.overridePatientSex || firstFile.dicomPatientSex || '',
          modality: firstFile.dicomModality || 'OT',
          studyDate: firstFile.dicomStudyDate || '',
          studyDescription: firstFile.dicomStudyDescription || '',
          institutionName: firstFile.dicomInstitutionName || '',
          imageCount: files.length,
          hasOverrides,
        }
      })
      // Sort by number of files (larger groups first)
      .sort((a, b) => b.imageCount - a.imageCount)
  }, [allPendingFiles])

  // Group processed files for display
  const processedGroups = useMemo((): StudyGroup[] => {
    const files = Array.isArray(processedFiles) ? processedFiles : []
    const groups: Record<string, HotFolderFile[]> = {}

    files.forEach((file) => {
      const key = file.dicomStudyInstanceUid || `no-study-${file.id}`
      if (!groups[key]) groups[key] = []
      groups[key].push(file)
    })

    return Object.entries(groups)
      .map(([studyUid, files]): StudyGroup => {
        const firstFile = files[0]

        let patientName = 'Sin nombre'
        if (firstFile.overridePatientFirstName || firstFile.overridePatientLastName) {
          const parts = [
            firstFile.overridePatientFirstName,
            firstFile.overridePatientMiddleName,
            firstFile.overridePatientLastName,
            firstFile.overridePatientSecondLastName,
          ].filter(Boolean)
          patientName = parts.join(' ')
        } else if (firstFile.dicomPatientName) {
          patientName = formatDicomPatientName(firstFile.dicomPatientName)
        }

        return {
          studyUid,
          files,
          patientName,
          documentType: firstFile.overrideDocumentType || '',
          documentNumber: firstFile.overrideDocumentNumber || firstFile.dicomPatientId || '',
          birthDate: firstFile.overridePatientBirthDate || firstFile.dicomPatientBirthDate || '',
          sex: firstFile.overridePatientSex || firstFile.dicomPatientSex || '',
          modality: firstFile.dicomModality || 'OT',
          studyDate: firstFile.dicomStudyDate || '',
          studyDescription: firstFile.dicomStudyDescription || '',
          institutionName: firstFile.dicomInstitutionName || '',
          imageCount: files.length,
          hasOverrides: false,
        }
      })
      .sort((a, b) => b.imageCount - a.imageCount)
  }, [processedFiles])

  // Mutations
  const scanMutation = useScanHotFolder()
  const resetMutation = useResetHotFolder()
  const skipFileMutation = useSkipFile()

  const refetchAll = () => {
    refetchStats()
    refetchPending()
    refetchDetected()
    refetchProcessing()
    refetchProcessed()
  }

  // Handlers
  const handleScan = () => {
    if (!id) return
    scanMutation.mutate(id, {
      onSuccess: (result) => {
        toast.success(`Escaneo completado: ${result.newFiles} nuevos archivos`)
        refetchAll()
      },
      onError: (err) => {
        toast.error(getErrorMessage(err, 'Error al escanear'))
      },
    })
  }

  const handleReset = (deleteAll: boolean = false) => {
    if (!id) return
    resetMutation.mutate({ hotFolderId: id, deleteAll }, {
      onSuccess: (result) => {
        toast.success(result.message)
        setShowResetConfirm(false)
        refetchAll()
      },
      onError: (err) => {
        toast.error(getErrorMessage(err, 'Error al resetear'))
      },
    })
  }

  const handleProcessGroup = (group: StudyGroup) => {
    if (group.files.length === 0) return
    setGroupToProcess(group.files)
  }

  const handleGroupProcessConfirmed = (result: { success: number; errors: number }) => {
    if (result.errors > 0) {
      toast.warning(
        `Estudio cargado con ${result.success} imágenes. ${result.errors} archivo(s) con error.`
      )
    } else {
      toast.success(
        `Estudio cargado exitosamente con ${result.success} imágenes`
      )
    }
    setGroupToProcess(null)
    refetchAll()
  }

  const handleSkipGroup = async (group: StudyGroup) => {
    setSkippingGroupId(group.studyUid)
    try {
      // Skip all files in the group
      await Promise.all(
        group.files.map(file => skipFileMutation.mutateAsync(file.id))
      )
      toast.success(`${group.files.length} archivo(s) omitido(s)`)
      refetchAll()
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al omitir archivos'))
    } finally {
      setSkippingGroupId(null)
    }
  }

  if (!id) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-[var(--foreground-muted)]" />
        <p className="mt-2 text-[var(--foreground-muted)]">ID de carpeta no válido</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(APP_ROUTES.ADMIN.HOT_FOLDERS)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {hotFolder?.name || 'Archivos'}
            </h1>
            <p className="text-[var(--foreground-muted)] font-mono text-sm">
              {hotFolder?.folderPath}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowResetConfirm(true)}
            disabled={resetMutation.isPending}
            className="text-amber-600 dark:text-amber-400 border-amber-300 hover:bg-amber-50 dark:border-amber-800 dark:hover:bg-amber-900/20"
          >
            {resetMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Resetear Estados
          </Button>
          <Button variant="outline" onClick={handleScan} disabled={scanMutation.isPending}>
            {scanMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Escanear Ahora
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <Files className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-[var(--foreground-muted)]">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                  <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{studyGroups.length}</p>
                  <p className="text-sm text-[var(--foreground-muted)]">Estudios Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <FileCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.completed}</p>
                  <p className="text-sm text-[var(--foreground-muted)]">Procesados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.failed}</p>
                  <p className="text-sm text-[var(--foreground-muted)]">Errores</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'pending' | 'processed')}>
        <TabsList>
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="h-4 w-4" />
            Pendientes
            {studyGroups.length > 0 && (
              <Badge variant="warning" className="ml-1">
                {studyGroups.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="processed" className="gap-2">
            <FileCheck className="h-4 w-4" />
            Procesados
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          {studyGroups.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="mx-auto h-12 w-12 text-[var(--foreground-muted)] opacity-50" />
                <p className="mt-2 text-[var(--foreground-muted)]">
                  No hay estudios pendientes
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {studyGroups.map((group) => (
                <StudyGroupCard
                  key={group.studyUid}
                  group={group}
                  onEdit={setEditingGroup}
                  onProcess={handleProcessGroup}
                  onSkip={handleSkipGroup}
                  isSkipping={skippingGroupId === group.studyUid}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="processed" className="mt-4">
          {processedGroups.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileCheck className="mx-auto h-12 w-12 text-[var(--foreground-muted)] opacity-50" />
                <p className="mt-2 text-[var(--foreground-muted)]">
                  No hay estudios procesados
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {processedGroups.map((group) => (
                <Card key={group.studyUid} className="bg-green-50 dark:bg-green-950/40/30 dark:bg-green-900/10 border-green-200 dark:border-green-800">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{group.patientName}</h3>
                          <p className="text-sm text-[var(--foreground-muted)]">
                            {group.documentNumber || 'Sin documento'}
                          </p>
                        </div>
                        <Badge variant="outline">
                          {getModalityDisplayName(group.modality)}
                        </Badge>
                        {group.studyDate && (
                          <span className="text-sm text-[var(--foreground-muted)]">
                            {formatDicomDate(group.studyDate)}
                          </span>
                        )}
                        <Badge variant="success">
                          {group.imageCount} {group.imageCount === 1 ? 'imagen' : 'imágenes'}
                        </Badge>
                      </div>
                      <Badge variant="success">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Cargado
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Group Edit Modal */}
      <GroupEditModal
        open={!!editingGroup}
        onOpenChange={(open) => !open && setEditingGroup(null)}
        group={editingGroup}
        onSuccess={refetchAll}
      />

      {/* Group Process Preview Modal */}
      <GroupProcessPreviewModal
        files={groupToProcess || []}
        isOpen={!!groupToProcess}
        onClose={() => setGroupToProcess(null)}
        onConfirm={handleGroupProcessConfirmed}
      />

      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogClose onClose={() => setShowResetConfirm(false)} />
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <RefreshCw className="h-5 w-5" />
              Resetear Archivos
            </DialogTitle>
            <DialogDescription>
              Elija una opción para resetear los archivos de esta carpeta.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Option 1: Reset status only */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
              <p className="text-sm text-blue-700 dark:text-blue-400 mb-2">
                <strong>Resetear Estados (Recomendado)</strong>
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-500">
                Cambia los archivos COMPLETADOS y FALLIDOS a estado DETECTADO
                para poder reprocesarlos. Los datos del escaneo se mantienen.
              </p>
              <Button
                className="w-full mt-2"
                onClick={() => handleReset(false)}
                disabled={resetMutation.isPending}
              >
                {resetMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Reseteando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Resetear para Reprocesar
                  </>
                )}
              </Button>
            </div>

            {/* Option 2: Delete all */}
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
              <p className="text-sm text-red-700 dark:text-red-400 mb-2">
                <strong>Eliminar Todo</strong>
              </p>
              <p className="text-xs text-red-600 dark:text-red-500">
                Elimina todos los registros de archivos. Requiere volver a escanear
                la carpeta. Los estudios y pacientes creados NO se eliminan.
              </p>
              <Button
                variant="destructive"
                className="w-full mt-2"
                onClick={() => handleReset(true)}
                disabled={resetMutation.isPending}
              >
                {resetMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar y Re-escanear
                  </>
                )}
              </Button>
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setShowResetConfirm(false)} className="w-full">
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
